import { createContext } from "react";
import { AuthStateProps, AcquireTokenFunction } from "@konecorp/ui-library";
import { TrainingHeadersType } from "../components/TrainingsHeaderView";

export type ErrorMessage = {
  message: string;
  error: unknown;
};

export type UserPreferenceType = {
  languagePreference: string;
  businessType?: number | null;
  area?: number | null;
  frontline?: number | null;
  country?: number[] | null;
  supplierType?: string | null;
};

export type QualificationsType = {
  title: string;
  completionDate: string;
  expirationDate: string;
  type: number;
  trainingId: number;
  qualificationTypeId: number;
  state?: string;
  validForYears?: number | null;
  qualificationLevel?: string;
  qualificationLevelId?: number | null;
};

export type TrainingTypes = {
  GLOBAL_SAFETY: number;
  LOCAL_SAFETY: number;
  METHOD: number;
};

export type BreadcrumbsType = {
  key: string;
  value: number;
  label: string;
};

export type BusinessType = {
  businessTypeId: number;
  businessTypeName: string;
  status: string;
};

export interface ContextState {
  authState: AuthStateProps;
  isLoading: boolean;
  acquireToken?: AcquireTokenFunction;
  errorMessage: ErrorMessage | null;
  countryList: string[];
  trainingHeaders: TrainingHeadersType[];
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loadingStack: string[];
  beginLoading: (token?: string) => string;
  endLoading: (token?: string) => void;
  withLoading: <T>(callback: () => Promise<T>, token?: string) => Promise<T>;
  setTrainingHeaders: (trainingHeaders: TrainingHeadersType[]) => void;
  setCountryList: (countryList: string[]) => void;
  updateAuthState: (authState: AuthStateProps) => void;
  updateIsLoading: (status: boolean) => void;
  updateAcquireToken: (newFunction: () => AcquireTokenFunction) => void;
  updateErrorMessage: (errorMessage: ErrorMessage | null) => void;
  country: string[]; // Added the missing 'country' property
  setCountry: React.Dispatch<React.SetStateAction<string[]>>; // Added the setter for 'country'
  frontline: string[]; // Added the missing 'frontline' property
  setFrontline: React.Dispatch<React.SetStateAction<string[]>>; // Added the setter for 'frontline'
  frontlineId: string; // Added the missing 'frontlineId' property
  setFrontlineId: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'frontlineId'
  regionId: string; // Added the missing 'regionId' property
  setRegionId: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'regionId'
  supplierType: string; // Added the missing 'supplierType' property
  setSupplierType: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'supplierType'
  formattedCountryCodes: string[]; // Added the missing 'formattedCountryCodes' property
  setFormattedCountryCodes: React.Dispatch<React.SetStateAction<string[]>>; // Added the setter for 'formattedCountryCodes'
  blockReason: string; // Added the missing 'blockReason' property
  setBlockReason: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'blockReason'
  bulkUploadStatus: boolean; // Added the missing 'bulkUploadStatus' property
  setBulkUploadStatus: React.Dispatch<React.SetStateAction<boolean>>; // Added the setter for 'bulkUploadStatus'
  alertState: "success" | "error" | "info" | "warning" | ""; // Added the missing 'alertState' property
  setAlertState: React.Dispatch<
    React.SetStateAction<"success" | "error" | "info" | "warning" | "">
  >; // Added the setter for 'alertState'
  alertMessage: string; // Added the missing 'alertMessage' property
  setAlertMessage: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'alertMessage'
  userFirstName: string; // Added the missing 'userFirstName' property
  setUserFirstName: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'userFirstName'
  userLastName: string; // Added the missing 'userLastName' property
  setUserLastName: React.Dispatch<React.SetStateAction<string>>; // Added the setter for 'userLastName'
  userPreference: UserPreferenceType; // Added the missing 'userPreference' property
  setUserPreference: React.Dispatch<React.SetStateAction<UserPreferenceType>>; // Added the setter for 'userPreference'
  qualificationsList: QualificationsType[]; // Added the missing 'qualificationsList' property
  setQualificationsList: React.Dispatch<
    React.SetStateAction<QualificationsType[]>
  >; // Added the setter for 'qualificationsList'
  passportTypeId: number; // Added the missing 'passportLevel' property
  setPassportTypeId: React.Dispatch<React.SetStateAction<number>>; // Added the setter for 'passportLevel'
  qualificationTypeId: number; // Added the missing 'qualificationType' property
  setQualificationTypeId: React.Dispatch<React.SetStateAction<number>>; // Added the setter for 'qualificationType'
  trainingTypes: TrainingTypes; // Added the missing 'trainingTypes' property
  setTrainingTypes: React.Dispatch<React.SetStateAction<TrainingTypes>>; // Added the setter for 'trainingTypes'
  bulkUploadMode: "upload" | "download"; // Added the missing 'bulkUploadMode' property
  setBulkUploadMode: React.Dispatch<
    React.SetStateAction<"upload" | "download">
  >; // Added the setter for 'bulkUploadMode'
  breadcrumbs: BreadcrumbsType[]; // Added the missing 'breadcrumbs' property
  setBreadcrumbs: React.Dispatch<React.SetStateAction<BreadcrumbsType[]>>; // Added the setter for 'breadcrumbs'
  isBreadcrumbClicked: boolean;
  setIsBreadcrumbClicked: React.Dispatch<React.SetStateAction<boolean>>;
  // Business Type state
  selectedBusinessType: string; // Selected business type code ('DELIVERY' or 'SERVICE')
  setSelectedBusinessType: React.Dispatch<React.SetStateAction<string>>;
  selectedBusinessTypeId: number; // Selected business type ID
  setSelectedBusinessTypeId: React.Dispatch<React.SetStateAction<number>>;
  businessTypesList: BusinessType[]; // List of available business types
  setBusinessTypesList: React.Dispatch<React.SetStateAction<BusinessType[]>>;
  isReview: boolean; // State to determine if user is in review mode
  setIsReview: React.Dispatch<React.SetStateAction<boolean>>;
  isAgreed: boolean; // State to track if user has agreed to terms
  setIsAgreed: React.Dispatch<React.SetStateAction<boolean>>;
  statusEndDate: string; // State to track the end date of a status
  setStatusEndDate: React.Dispatch<React.SetStateAction<string>>;
  adminRole: string | null; // State to track the admin role of the user
  setAdminRole: React.Dispatch<React.SetStateAction<string | null>>; // Setter for the admin role of the user
  /**
   * What this caller may do, exactly as the backend resolved it.
   *
   * Comes from GET /v1/me, which reads the authorizer context - so it is the
   * same list `permissionMiddleware` will judge every request against, sourced
   * from ad_group_permissions. Screens gate on these rather than on the AD role
   * name, so granting a group access to something is a data change and needs no
   * release.
   */
  permissions: string[];
  setPermissions: React.Dispatch<React.SetStateAction<string[]>>;
}

export default createContext({} as ContextState);
